// ======================================================================
// 📁 File: src/modules/ppe/pages/engineering/product-variant/ProductVariantListPage.jsx
// 📝 Purpose:
//   Product Variant Master – List Page
//   - Gradient header (ERP standard)
//   - KPI bar
//   - Filters (search / status / family)
//   - Table view
//   - Drawer-based Create / Edit
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";
import axiosClient from "../../../../../api/axiosClient";

import ProductVariantKpiBar from "../../../components/engineering/product-variant/ProductVariantKpiBar";
import ProductVariantDrawer from "../../../components/engineering/product-variant/ProductVariantDrawer";

export default function ProductVariantListPage() {
  const [rows, setRows] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL"); // ALL | ACTIVE | INACTIVE
  const [familyId, setFamilyId] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [editingRow, setEditingRow] = useState(null);

  // ------------------------------------------------------------
  // Load Product Families (for filter + drawer)
  // ------------------------------------------------------------
  const loadFamilies = useCallback(async () => {
    const res = await axiosClient.get(
      "/api/ppe/masters/product-families"
    );
    setFamilies(res.data?.data || []);
  }, []);

  // ------------------------------------------------------------
  // Load Product Variants
  // ------------------------------------------------------------
  const loadVariants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/product-variants",
        {
          params: {
            q: search || undefined,
            is_active:
              status === "ALL"
                ? undefined
                : status === "ACTIVE"
                ? 1
                : 0,
            product_family_id: familyId || undefined,
          },
        }
      );

      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  }, [search, status, familyId]);

  useEffect(() => {
    loadFamilies();
    loadVariants();
  }, [loadFamilies, loadVariants]);

  // ------------------------------------------------------------
  // Drawer handlers
  // ------------------------------------------------------------
  const openCreate = () => {
    setEditingRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Product Variant Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Engineering configuration
          </div>
        </div>

        <button className="btn btn-light" onClick={openCreate}>
          + Create Variant
        </button>
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <ProductVariantKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <input
          className="form-control"
          placeholder="Search Variant"
          style={{ maxWidth: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 200 }}
          value={familyId}
          onChange={(e) => setFamilyId(e.target.value)}
        >
          <option value="">All Product Families</option>
          {families.map((pf) => (
            <option key={pf.id} value={pf.id}>
              {pf.name}
            </option>
          ))}
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button className="btn btn-primary" onClick={loadVariants}>
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Variant Code</th>
                <th>Name</th>
                <th>Product Family</th>
                <th>Drawing</th>
                <th>Routing</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No product variants found
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.variant_code}</td>
                  <td>{r.name}</td>
                  <td>
                    {families.find((f) => f.id === r.product_family_id)
                      ?.name || "-"}
                  </td>
                  <td>{r.drawing_required ? "Yes" : "No"}</td>
                  <td>{r.routing_required ? "Yes" : "No"}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openEdit(r)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Drawer */}
      {/* ====================================================== */}
      <ProductVariantDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={editingRow}
        productFamilies={families}
        onClose={() => setDrawerOpen(false)}
        onSaved={loadVariants}
      />
    </div>
  );
}
